import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function ServicesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      category: "Branding & Visual Identity",
      icon: "ri-palette-line",
      color: "from-orange-500 to-red-500",
      path: "/services/branding",
      services: ["Branding", "Logo Design", "Packaging Design", "Campaign Identity", "Graphic & Motion Design"]
    },
    {
      category: "Data Driven Growth Marketing", 
      icon: "ri-line-chart-line",
      color: "from-red-500 to-pink-500",
      path: "/services/marketing",
      services: ["AI Integrated Social Media Marketing", "Optimized PPC Campaigns", "AI Integrated Google Ads Campaigns", "Data Analytics and Insights", "Content Production"]
    },
    {
      category: "AI & Agentic Solutions",
      icon: "ri-robot-line", 
      color: "from-pink-500 to-purple-500",
      path: "/services/ai",
      services: ["AI Automated Website Development", "Personalized AI Agents (Online & Offline)", "Automated AI Telecalling", "Automated Email Marketing", "Automated Media Outreach"]
    },
    {
      category: "3D Multi Colour Development & Printing",
      icon: "ri-3d-view-line",
      color: "from-purple-500 to-blue-500", 
      path: "/services/threed",
      services: ["3D Model Development", "Multi-Colour 3D Printing", "Industrial Design & Prototyping", "Architectural & Product Visualization"]
    },
    {
      category: "Web3 & Innovation Consultation",
      icon: "ri-global-line",
      color: "from-blue-500 to-cyan-500",
      path: "/services/web3",
      services: ["Metaverse Strategy Development", "NFT Minting & Strategy", "Blockchain & Decentralized Applications Consultation", "Future Trends Analysis"]
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="services" 
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-orange-500 rounded-full animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              AI-Powered Solutions
            </span>
            <br />
            Designed for Your Success
          </h2>
          <h3 className="text-2xl md:text-3xl font-semibold text-orange-400 mb-6">
            Unlock Intelligent Growth Across Every Touchpoint
          </h3>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Discover how our suite of AI-integrated services can revolutionize your marketing, operations, and customer engagement.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Link
              key={index}
              to={service.path}
              className={`group relative transition-all duration-700 delay-${index * 100} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              onMouseEnter={() => setHoveredService(index)}
              onMouseLeave={() => setHoveredService(null)}
            >
              <div className="relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-orange-500/20 hover:border-orange-500/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/20 cursor-pointer h-full">
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                
                <div className={`relative w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r ${service.color} flex items-center justify-center group-hover:animate-pulse`}>
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>

                <h3 className="text-xl font-bold text-white mb-6 text-center group-hover:text-orange-400 transition-colors duration-300">
                  {service.category}
                </h3>

                <ul className="space-y-3">
                  {service.services.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className={`flex items-center text-gray-300 group-hover:text-white transition-all duration-300 delay-${itemIndex * 50}`}
                    >
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.color} mr-3 group-hover:animate-pulse`}></div>
                      <span className="text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                  <i className="ri-arrow-right-line text-orange-400 text-xl"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <p className="text-lg text-gray-400 mb-6">
            Ready to transform your business with AI-powered solutions?
          </p>
          <Link to="/services" className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer inline-block">
            <span className="flex items-center gap-3">
              Explore All Services
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </span>
          </Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        .animate-twinkle {
          animation: twinkle 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}